import React, { useState, createContext, useContext } from 'react';
import { api } from './utils/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LogSession from './pages/LogSession';
import Sessions from './pages/Sessions';
import ReportInstructor from './pages/ReportInstructor';
import ReportPilot from './pages/ReportPilot';
import ReportSquawk from './pages/ReportSquawk';
import Admin from './pages/Admin';
import './App.css';

export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

const NAV = [
  { path: 'dashboard',    label: 'Dashboard' },
  { path: 'log',          label: 'Log Session' },
  { path: 'sessions',     label: 'Session History' },
  { path: 'report-inst',  label: 'Instructor Report' },
  { path: 'report-pilot', label: 'Training Report' },
  { path: 'report-sqk',   label: 'Squawk Report' },
];

export default function App() {
  const [user, setUser] = useState(() => {
    const t = localStorage.getItem('simlog_token');
    const u = localStorage.getItem('simlog_user');
    return (t && u) ? JSON.parse(u) : null;
  });
  const [page,       setPage]       = useState('dashboard');
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [pwModal,    setPwModal]    = useState(false);
  const [pwForm,     setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwError,    setPwError]    = useState('');
  const [pwSuccess,  setPwSuccess]  = useState('');
  const [pwBusy,     setPwBusy]     = useState(false);

  function login(token, userData) {
    localStorage.setItem('simlog_token', token);
    localStorage.setItem('simlog_user', JSON.stringify(userData));
    setUser(userData);
    setPage('dashboard');
  }

  function logout() {
    localStorage.removeItem('simlog_token');
    localStorage.removeItem('simlog_user');
    setUser(null);
  }

  function navigate(path) {
    setPage(path);
    setMenuOpen(false);
  }

  async function changePassword() {
    setPwError(''); setPwSuccess('');
    if (!pwForm.current)          { setPwError('Current password is required.'); return; }
    if (pwForm.next.length < 6)   { setPwError('New password must be at least 6 characters.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError('New passwords do not match.'); return; }
    setPwBusy(true);
    try {
      await api.changePassword(pwForm.current, pwForm.next);
      setPwSuccess('Password changed successfully!');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e) { setPwError(e.message || 'Incorrect current password.'); }
    setPwBusy(false);
  }

  if (!user) return <Login onLogin={login} />;

  const navItems = user.role === 'admin'
    ? [...NAV, { path: 'admin', label: 'Admin' }]
    : NAV;

  return (
    <AuthContext.Provider value={{ user, logout }}>
      <div className="app">
        <header className="app-header">
          <div className="header-brand">
            <img src="/occ-logo-40.png" alt="OCC" className="header-logo-img" />
            <span className="header-title">Redbird CJ1+ Simulator Log</span>
          </div>

          {/* Desktop nav */}
          <nav className="header-nav desktop-nav">
            {navItems.map(n => (
              <button
                key={n.path}
                className={`nav-btn ${page === n.path ? 'active' : ''}`}
                onClick={() => navigate(n.path)}
              >
                {n.label}
              </button>
            ))}
            <button className="nav-btn account-btn" onClick={() => { setPwModal(true); setPwError(''); setPwSuccess(''); setPwForm({ current: '', next: '', confirm: '' }); }}>Account</button>
            <button className="nav-btn logout-btn" onClick={logout}>Logout</button>
          </nav>

          {/* Hamburger button — mobile/tablet only */}
          <button
            className="hamburger-btn"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
          >
            <span className={`hamburger-icon ${menuOpen ? 'open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        </header>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="mobile-menu">
            {navItems.map(n => (
              <button
                key={n.path}
                className={`mobile-nav-btn ${page === n.path ? 'active' : ''}`}
                onClick={() => navigate(n.path)}
              >
                {n.label}
              </button>
            ))}
            <button className="mobile-nav-btn" onClick={() => { setMenuOpen(false); setPwModal(true); setPwError(''); setPwSuccess(''); setPwForm({ current: '', next: '', confirm: '' }); }}>
              Account / Change Password
            </button>
            <button className="mobile-nav-btn mobile-logout" onClick={() => { logout(); setMenuOpen(false); }}>
              Logout
            </button>
          </div>
        )}

        {/* Change Password modal */}
        {pwModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPwModal(false)}>
            <div className="modal">
              <h3>Change Password</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Logged in as <strong>{user.name}</strong> ({user.email})</p>
              {pwError   && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{pwError}</div>}
              {pwSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{pwSuccess}</div>}
              {!pwSuccess && (<>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label>Current Password *</label>
                  <input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} autoFocus />
                </div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label>New Password *</label>
                  <input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label>Confirm New Password *</label>
                  <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && changePassword()} />
                </div>
              </>)}
              <div className="btn-row">
                {!pwSuccess && <button className="btn btn-primary" onClick={changePassword} disabled={pwBusy}>{pwBusy ? 'Saving...' : 'Change Password'}</button>}
                <button className="btn btn-secondary" onClick={() => setPwModal(false)}>{pwSuccess ? 'Close' : 'Cancel'}</button>
              </div>
            </div>
          </div>
        )}

        <main className="app-main">
          {page === 'dashboard'    && <Dashboard onNavigate={setPage} />}
          {page === 'log'          && <LogSession onSaved={() => setPage('sessions')} />}
          {page === 'sessions'     && <Sessions onNavigate={setPage} />}
          {page === 'report-inst'  && <ReportInstructor />}
          {page === 'report-pilot' && <ReportPilot />}
          {page === 'report-sqk'   && <ReportSquawk />}
          {page === 'admin'        && <Admin />}
        </main>
      </div>
    </AuthContext.Provider>
  );
}
